/**
 * Third-party error text — Tally's, and the IRN portal's — rendered **escaped and unchanged**
 * (`010` R3, FR-009; `011` FR-011).
 *
 * ## Two properties in tension, and both are requirements
 *
 * It is third-party text going into a page, which is the ordinary shape of an injection bug. It is
 * also **the only thing** that tells an Accounts user which ledger name to fix — §14.5 retains it
 * precisely so a person can act on it.
 *
 * The ordinary fix for the first — sanitise it into something friendly — defeats the second
 * entirely. So: React's own escaping (the text is a child, never `dangerouslySetInnerHTML`), and
 * **no other transformation at all**. Not trimmed, not sentence-cased, not truncated, no trailing
 * full stop added, no "Error:" prefix removed. The characters that arrive are the characters shown.
 *
 * `<pre>` rather than `<p>`: this text carries its own line breaks and alignment, and the ledger
 * name that has a trailing space is exactly the bug being diagnosed. Monospaced so a space is
 * visible as a space. It wraps rather than scrolling off, because it is read, not code.
 */
export function VerbatimErrorText({
  children,
  /** Names the source for a screen reader — "Tally's response". */
  label,
}: {
  children: string;
  label?: string;
}) {
  return (
    <pre
      data-slot="verbatim-error-text"
      aria-label={label}
      className="border-muted bg-gray-100 text-foreground max-w-full overflow-x-auto rounded-lg border p-3 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap"
    >
      {children}
    </pre>
  );
}
